import { IMap } from 'core-ts-types';
import * as _4 from 'lodash4';
import { find, isEmpty, map } from 'lodash4';
import * as moment from 'moment';
import { APCourse } from '../builders/ap_course_builder';
import { APGenericSlot, NCRSlot } from '../builders/ap_generic_slot_builder';
import { APPlannedCourse, IAPPlannedCourseMeta } from '../builders/ap_planned_course_builder';
import { APTerm, IAPTermMeta, IAPTermPriorityReg } from '../builders/ap_term_builder';
import {
  APTermCourseStatusGroupStrategy,
  ITermCourseStatusGroup,
  ITermGenericSlotStatusGroup
} from '../strategies/ap_term_course_status_group_strategy';
import { APTermStatusStrategy } from '../strategies/ap_term_status_strategy';

import {
  ap_course_requisite_type,
  APCourseRequisiteReminderStrategy,
  IAPCourseRequisiteReminder
} from '../strategies/ap_course_requisite_reminder_strategy';
import { APProgramSlotServant } from './ap_program_slot_servant';

export class APTermServant {
  static getFutureTerms(termsWithMeta: Array<APTerm & IAPTermMeta>) {
    return _4.sortBy(_4.reject(termsWithMeta, { is_past_term: true }), 'rank');
  }

  static sortByAlertsAndTitleCourses(genericSlots: APGenericSlot[]): APGenericSlot[] {
    return _4.sortBy(genericSlots, 'programSlot.id');
  }

  static courseRequisiteMessages(course: IAPPlannedCourseMeta) {
    return _4.compact(
      APCourseRequisiteReminderStrategy.map((val: IAPCourseRequisiteReminder, key: ap_course_requisite_type) =>
        course[key].length > 0 ? val.reminder_template(course._course) : ``
      )
    );
  }

  static coursesMissingRequisites(term: APTerm) {
    return _4.compact(
      _4.reduce(
        term._plannedCourses,
        (messages: string[], course: APPlannedCourse & IAPPlannedCourseMeta) =>
          _4.concat(messages, APTermServant.courseRequisiteMessages(course)),
        []
      )
    );
  }

  static sortCourseBucketCategories(termGenericSlots: APGenericSlot[], term: APTerm) {
    const statusGroups = _4.mapValues(
      APTermCourseStatusGroupStrategy,
      (strategy: ITermCourseStatusGroup, objKey: string): ITermGenericSlotStatusGroup =>
        _4.assign({}, strategy, { genericSlots: [], groupKey: objKey })
    ) as any as Record<string, ITermGenericSlotStatusGroup>;
    _4.each(statusGroups, (statusGroup: ITermGenericSlotStatusGroup, prop: string) => {
      statusGroup.genericSlots = APTermServant.sortByAlertsAndTitleCourses(
        _4.filter(termGenericSlots, statusGroup.filter)
      );
    });

    // NOTE: Planned courses
    // Has courses with sections picked - marked gray
    // When registration is open - these sections become 'Ready to register'
    const isRegistrationOpen = APTermServant.isRegistrationOpen(term);
    if (isRegistrationOpen) {
      const planned = statusGroups['PLANNED'];
      const slotsWithSectionsPicked = _4.partition(
        planned.genericSlots,
        (slot: Exclude<APGenericSlot, NCRSlot>) =>
          _4.get(slot.plannedCourse as APPlannedCourse & IAPPlannedCourseMeta, 'status') === 'SECTION_PICKED'
      );
      statusGroups['READY_TO_REGISTER'].genericSlots = slotsWithSectionsPicked[0];
      planned.genericSlots = slotsWithSectionsPicked[1];
    } else {
      statusGroups['READY_TO_REGISTER'].genericSlots = [];
    }
    return statusGroups;
  }

  static isRegistrationOpen(term: APTerm) {
    return APTermStatusStrategy.isEqual(term._termStatus, APTermStatusStrategy.getByKey('REG_OPEN'));
  }

  static isPriorityRegistrationOpen(term: APTerm) {
    return APTermStatusStrategy.isEqual(term._termStatus, APTermStatusStrategy.getByKey('PRIORITY_REG_OPEN'));
  }

  static isCompleted(term: APTerm) {
    return APTermStatusStrategy.isEqual(term._termStatus, APTermStatusStrategy.getByKey('TERM_COMPLETED'));
  }

  static hasCoursesWithStatus(term: APTerm) {
    // Check if any course has a non default status.
    return _4.some(
      term._plannedCourses,
      (course: APCourse) => _4.isEmpty(course.status) || _4.isEqual(course.status, 'DEFAULT')
    );
  }

  static hasAdvisorLockedCourseExist(term: APTerm) {
    return _4.some(term._genericSlots, { isLockedByAdvisor: true });
  }

  static isRegistrationButtonDisabled(term: APTerm): any {
    return !APTermServant.hasAvailableSections(term);
  }

  static hasDefaultRegistrationDates(term: APTerm) {
    return term.registration_end_dt.year() === 1900 || term.registration_start_dt.year() === 1900;
  }

  static hasAvailableSections(term: APTerm) {
    return term.num_sections > 0;
  }

  static hasPriorityReg(term: APTerm): boolean {
    return !isEmpty(term.priority_registration_start_dt) && !isEmpty(term.priority_registration_end_dt);
  }

  static inCountdownPeriod(term: APTerm): boolean {
    if (!APTermServant.hasPriorityReg(term)) {
      return false;
    }
    const NUMBER_OF_WEEKS = 2;
    const countdownStart: moment.Moment = moment(term.priority_registration_start_dt as moment.Moment).subtract(
      NUMBER_OF_WEEKS,
      'weeks'
    );
    const countdownEnd = moment(term.priority_registration_start_dt as moment.Moment);

    const now = moment();
    return now.isBetween(countdownStart, countdownEnd);
  }

  static inPriorityPeriod(term: APTerm) {
    return (
      APTermServant.hasPriorityReg(term) &&
      moment().isBetween(
        term.priority_registration_start_dt as moment.Moment,
        term.priority_registration_end_dt as moment.Moment
      )
    );
  }

  static hasCreditCapExceeded(term: APTerm): boolean {
    if (_4.isNull(term.credit_cap)) {
      return false;
    }
    return APProgramSlotServant.hasCreditRangeExceed(term._genericSlots, term.credit_cap as number);
  }

  static getTermsWithPriorityRegistrationDatesAssigned(
    terms: IMap<APTerm>,
    priorityRegTermsData: IAPTermPriorityReg[]
  ): APTerm[] {
    return map(terms, (term: APTerm) => {
      const priorityRegTermData: IAPTermPriorityReg = find(priorityRegTermsData, { term: term.id });
      if (!priorityRegTermData) {
        return term;
      }
      // priority reg start, end time comes are in UTC from api, so converting them to local timezone
      const timeZoneFormat = 'YYYY-MM-DDThh:mm:ssZ';
      term.priority_registration_start_dt = moment.utc(priorityRegTermData.start_ts, timeZoneFormat).local();
      if (isEmpty(priorityRegTermData.end_ts)) {
        term.priority_registration_end_dt = moment(term.registration_start_dt);
      } else {
        term.priority_registration_end_dt = moment.utc(priorityRegTermData.end_ts, timeZoneFormat).local();
      }
      return term;
    });
  }

  static getSectionPickedAndPlannedCourses(term: APTerm): APPlannedCourse[] {
    const allPlannedCoursesInTerm: APPlannedCourse[] = _4.get(term, '_plannedCourses', []);
    return _4.filter(
      allPlannedCoursesInTerm,
      (plannedCourse) => !_4.includes(['REGISTERED', 'IN_PROGRESS', 'COMPLETED', 'WAITLISTED'], plannedCourse.status)
    );
  }

  static getAllLockedPlannedCourses(term: APTerm) {
    const allPlannedCourses: APPlannedCourse[] = this.getSectionPickedAndPlannedCourses(term);
    return _4.filter(allPlannedCourses, 'is_locked');
  }
  static getAllUnLockedPlannedCourses(term: APTerm) {
    const allPlannedCourses: APPlannedCourse[] = this.getSectionPickedAndPlannedCourses(term);
    return _4.reject(allPlannedCourses, 'is_locked');
  }
}



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-academic-planner/src/global/servants/ap_term_servant.ts